// 用戶過濾系統 - 排除開發者和測試用戶的數據
import { setUserProperty } from './firebaseAnalytics';

// 開發者和測試用戶配置
interface UserFilterConfig {
  // 開發者用戶標識
  developerEmails: string[];
  developerIPs: string[];
  developerUserAgents: string[];
  
  // 測試用戶標識
  testUserIds: string[];
  testSessions: string[];
  
  // 排除條件
  excludeFromAnalytics: boolean;
  excludeFromChatAnalytics: boolean;
  excludeFromErrorTracking: boolean;
}

// 默認配置
const defaultConfig: UserFilterConfig = {
  developerEmails: [
    // 在這裡添加您的開發者郵箱
    'your-email@example.com',
  ],
  developerIPs: [
    // 在這裡添加您的IP地址
    '127.0.0.1',
    'localhost',
  ],
  developerUserAgents: [
    // 開發者常用的User Agent模式
    'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
  ],
  testUserIds: [
    // 測試用戶ID
    'test_user_1',
    'test_user_2',
  ],
  testSessions: [
    // 測試會話ID
    'test_session_',
  ],
  excludeFromAnalytics: true,
  excludeFromChatAnalytics: true,
  excludeFromErrorTracking: false, // 錯誤追蹤通常需要保留
};

class UserFilter {
  private static instance: UserFilter;
  private config: UserFilterConfig;
  private currentUserInfo: any = null;

  constructor() {
    this.config = { ...defaultConfig };
    this.initializeUserInfo();
  }

  static getInstance(): UserFilter {
    if (!UserFilter.instance) {
      UserFilter.instance = new UserFilter();
    }
    return UserFilter.instance;
  }

  // 初始化用戶信息
  private initializeUserInfo() {
    this.currentUserInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      timestamp: new Date().toISOString(),
      // 注意：無法直接獲取IP地址，需要通過其他方式
    };
  }

  // 檢查是否為開發者用戶
  isDeveloperUser(): boolean {
    // 檢查User Agent
    const isDeveloperUA = this.config.developerUserAgents.some(ua => 
      this.currentUserInfo.userAgent.includes(ua)
    );

    // 檢查本地開發環境
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.includes('localhost');

    // 檢查開發者標識（可以通過localStorage設置）
    const developerFlag = localStorage.getItem('surepal_developer_mode') === 'true';

    return isDeveloperUA || isLocalhost || developerFlag;
  }

  // 檢查是否為測試用戶
  isTestUser(userId?: string, sessionId?: string): boolean {
    // 檢查測試用戶ID
    if (userId && this.config.testUserIds.includes(userId)) {
      return true;
    }

    // 檢查測試會話ID
    if (sessionId && this.config.testSessions.some(testSession => 
      sessionId.startsWith(testSession)
    )) {
      return true;
    }

    // 檢查測試標識
    const testFlag = localStorage.getItem('surepal_test_mode') === 'true';
    return testFlag;
  }

  // 檢查是否應該排除數據
  shouldExcludeFromAnalytics(userId?: string, sessionId?: string): boolean {
    if (!this.config.excludeFromAnalytics) return false;
    
    return this.isDeveloperUser() || this.isTestUser(userId, sessionId);
  }

  // 檢查是否應該排除聊天分析
  shouldExcludeFromChatAnalytics(userId?: string, sessionId?: string): boolean {
    if (!this.config.excludeFromChatAnalytics) return false;
    
    return this.isDeveloperUser() || this.isTestUser(userId, sessionId);
  }

  // 檢查是否應該排除錯誤追蹤
  shouldExcludeFromErrorTracking(userId?: string, sessionId?: string): boolean {
    if (!this.config.excludeFromErrorTracking) return false;
    
    return this.isDeveloperUser() || this.isTestUser(userId, sessionId);
  }

  // 設置用戶屬性（用於識別）
  setUserIdentification(userId?: string, sessionId?: string) {
    const userType = this.isDeveloperUser() ? 'developer' : 
                    this.isTestUser(userId, sessionId) ? 'test' : 'regular';
    
    // 設置Firebase用戶屬性
    setUserProperty('user_type', userType);
    setUserProperty('is_developer', this.isDeveloperUser());
    setUserProperty('is_test_user', this.isTestUser(userId, sessionId));
    setUserProperty('exclude_from_analytics', this.shouldExcludeFromAnalytics(userId, sessionId));
  }

  // 過濾事件參數
  filterEventParameters(parameters: any, userId?: string, sessionId?: string): any {
    if (this.shouldExcludeFromAnalytics(userId, sessionId)) {
      return {
        ...parameters,
        filtered: true,
        filter_reason: this.isDeveloperUser() ? 'developer_user' : 'test_user',
      };
    }
    return parameters;
  }

  // 過濾聊天內容
  filterChatContent(content: string, userId?: string, sessionId?: string): string | null {
    if (this.shouldExcludeFromChatAnalytics(userId, sessionId)) {
      return null; // 不記錄聊天內容
    }
    return content;
  }

  // 更新配置
  updateConfig(newConfig: Partial<UserFilterConfig>) {
    this.config = { ...this.config, ...newConfig };
  }

  // 獲取當前配置
  getConfig(): UserFilterConfig {
    return { ...this.config };
  }

  // 獲取用戶信息摘要
  getUserInfoSummary(): any {
    return {
      isDeveloper: this.isDeveloperUser(),
      isTestUser: this.isTestUser(),
      shouldExcludeFromAnalytics: this.shouldExcludeFromAnalytics(),
      shouldExcludeFromChatAnalytics: this.shouldExcludeFromChatAnalytics(),
      shouldExcludeFromErrorTracking: this.shouldExcludeFromErrorTracking(),
      userAgent: this.currentUserInfo.userAgent,
      timestamp: this.currentUserInfo.timestamp,
    };
  }
}

// 導出實例
export const userFilter = UserFilter.getInstance();

// 導出便捷函數
export const shouldTrackEvent = (userId?: string, sessionId?: string): boolean => {
  return !userFilter.shouldExcludeFromAnalytics(userId, sessionId);
};

export const shouldTrackChat = (userId?: string, sessionId?: string): boolean => {
  return !userFilter.shouldExcludeFromChatAnalytics(userId, sessionId);
};

export const shouldTrackError = (userId?: string, sessionId?: string): boolean => {
  return !userFilter.shouldExcludeFromErrorTracking(userId, sessionId);
};

export const filterEventData = (data: any, userId?: string, sessionId?: string): any => {
  return userFilter.filterEventParameters(data, userId, sessionId);
};

export const filterChatData = (content: string, userId?: string, sessionId?: string): string | null => {
  return userFilter.filterChatContent(content, userId, sessionId);
};

export const setupUserFiltering = (userId?: string, sessionId?: string) => {
  userFilter.setUserIdentification(userId, sessionId);
};

// 開發者工具函數
export const enableDeveloperMode = () => {
  localStorage.setItem('surepal_developer_mode', 'true');
  console.log('🔧 Developer mode enabled - analytics will be filtered');
};

export const disableDeveloperMode = () => {
  localStorage.removeItem('surepal_developer_mode');
  console.log('🔧 Developer mode disabled');
};

export const enableTestMode = () => {
  localStorage.setItem('surepal_test_mode', 'true');
  console.log('🧪 Test mode enabled - analytics will be filtered');
};

export const disableTestMode = () => {
  localStorage.removeItem('surepal_test_mode');
  console.log('🧪 Test mode disabled');
};

export const getFilteringStatus = () => {
  return userFilter.getUserInfoSummary();
};

export default userFilter;
